import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

// Since we're using cookies, we don't need manual token management
// But keep these for backward compatibility
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// Request interceptor - no need to add Bearer token since we use cookies
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically included due to withCredentials: true
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Only logout for auth endpoint failures, not for third-party service errors
      // (e.g., JustSign API key issues should not log out the user)
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/') || url.includes('/users/');
      const isSigningEndpoint = url.includes('/signing/');

      if (isAuthEndpoint && !isSigningEndpoint) {
        clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  googleLogin: (token) => api.post('/auth/google', { token }),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token/${token}`),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyEmail: (token) => api.post(`/auth/verify-email/${token}`),

  // Token management helpers - simplified for cookie-based auth
  setTokens: (tokens) => {
    // Tokens are set as cookies by the backend, but keep this for compatibility
    console.log('Tokens set via cookies by backend');
  },
  
  clearTokens,

  // Check if user data exists in localStorage (since cookies aren't accessible via JS)
  isAuthenticated: () => !!localStorage.getItem('user'),
};

// User Preferences API
export const userPreferencesService = {
  getUserPreferences: () => api.get('/users/preferences'),
  updateUserPreferences: (preferences) => api.put('/users/preferences', preferences),
  getCalendarSettings: () => api.get('/users/preferences/calendar'),
  updateCalendarSettings: (settings) => api.put('/users/preferences/calendar', settings),
  getCasesTableSettings: () => api.get('/users/preferences/cases_table'),
  updateCasesTableSettings: (settings) => api.put('/users/preferences/cases_table', { preference_data: settings }),
  getCaseListReportConfigs: () => api.get('/users/preferences/case_list_report_configs'),
  updateCaseListReportConfigs: (data) => api.put('/users/preferences/case_list_report_configs', { preference_data: data }),
};

// Users API
export const userService = {
  getUsers: (includeDeactivated = false, roleFilter = null) => api.get('/users/', {
    params: { include_deactivated: includeDeactivated, ...(roleFilter ? { role_filter: roleFilter } : {}) }
  }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users/', userData),
  updateUser: (id, userData) => api.patch(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  // Role management
  getRoles: () => api.get('/auth/roles'),
  getUserRoles: (userId) => api.get(`/auth/user/${userId}/roles`),
  updateUserRoles: (userId, roleData) => api.put(`/auth/user/${userId}/roles`, roleData),
  // User status management
  updateUserStatus: (userId, statusData) => api.put(`/auth/user/${userId}/status`, statusData),
  // Firm membership management
  deactivateFromFirm: (userId) => api.post(`/auth/user/${userId}/firm/deactivate`),
  reactivateInFirm: (userId) => api.post(`/auth/user/${userId}/firm/reactivate`),
  // User invitation
  inviteUser: (inviteData) => api.post('/auth/invite-user', inviteData),
  resendInvitation: (userId) => api.post(`/auth/resend-invitation/${userId}`),
  // Invitation acceptance
  validateInvitationToken: (token) => api.get(`/auth/validate-invitation-token/${token}`),
  acceptInvitation: (token, password) => api.post('/auth/accept-invitation', {
    invitation_token: token,
    password: password
  }),
  // Permission management
  getAllPermissions: () => api.get('/auth/permissions'),
  getEffectivePermissions: (userId) => api.get(`/auth/user/${userId}/effective-permissions`),
  getCustomPermissions: (userId) => api.get(`/auth/user/${userId}/custom-permissions`),
  grantPermissions: (userId, permissionData) => api.post(`/auth/user/${userId}/custom-permissions`, permissionData),
  revokePermission: (userId, permissionId) => api.delete(`/auth/user/${userId}/custom-permissions/${permissionId}`),
};

// Clients API
export const clientService = {
  getClients: (params = {}) => api.get('/clients/', { params }),
  getClient: (id) => api.get(`/clients/${id}`),
  getBillingSummary: (id) => api.get(`/clients/${id}/billing-summary`),
  createClient: (clientData) => api.post('/clients/', clientData),
  updateClient: (id, clientData) => api.put(`/clients/${id}`, clientData),
  sendPortalInvite: (id) => api.post(`/clients/${id}/portal-invite`),
  requestSmsConsent: (id) => api.post(`/clients/${id}/request-sms-consent`),
};

// Intake Statuses API
export const intakeStatusService = {
  getIntakeStatuses: () => api.get('/intake-statuses/'),
  createIntakeStatus: (data) => api.post('/intake-statuses/', data),
  updateIntakeStatus: (id, data) => api.put(`/intake-statuses/${id}`, data),
  deleteIntakeStatus: (id) => api.delete(`/intake-statuses/${id}`),
};

// Cases API
export const caseService = {
  getCases: (params) => api.get('/cases/', { params }),
  getCase: (id) => api.get(`/cases/${id}`),
  createCase: (caseData) => api.post('/cases/', caseData),
  updateCase: (id, caseData) => api.put(`/cases/${id}`, caseData),
  getCasesByClient: (clientId) => api.get(`/cases/client/${clientId}`),
  getCasesByAttorney: (attorneyId) => api.get(`/cases/attorney/${attorneyId}`),
  getCasesGroupedByClient: () => api.get('/cases/grouped-by-client'),
  getFinancialSummary: (caseId) => api.get(`/cases/${caseId}/financial-summary`),
  getCasesTableView: (params) => api.get('/cases/table-view', { params }),
  getCaseVisibility: (caseId) => api.get(`/cases/${caseId}/visibility`),
  updateCaseVisibility: (caseId, data) => api.put(`/cases/${caseId}/visibility`, data),
};

// Case Notes API
export const caseNotesService = {
  getNotes: (caseId, params = {}) => api.get(`/cases/${caseId}/notes/`, { params }),
  getNote: (caseId, noteId) => api.get(`/cases/${caseId}/notes/${noteId}`),
  createNote: (caseId, noteData) => api.post(`/cases/${caseId}/notes/`, noteData),
  updateNote: (caseId, noteId, noteData) => api.put(`/cases/${caseId}/notes/${noteId}`, noteData),
  deleteNote: (caseId, noteId) => api.delete(`/cases/${caseId}/notes/${noteId}`),
  togglePin: (caseId, noteId) => api.patch(`/cases/${caseId}/notes/${noteId}/pin`),

  // Note type options
  getNoteTypes: () => [
    { value: 'general', label: 'General Note', icon: '📝' },
    { value: 'meeting', label: 'Meeting', icon: '🤝' },
    { value: 'call', label: 'Phone Call', icon: '📞' },
    { value: 'court', label: 'Court', icon: '⚖️' },
    { value: 'research', label: 'Research', icon: '🔍' },
    { value: 'discovery', label: 'Discovery', icon: '📋' },
    { value: 'filing', label: 'Filing', icon: '📁' },
    { value: 'other', label: 'Other', icon: '📌' },
  ],

  getNoteTypeIcon(type) {
    const types = this.getNoteTypes();
    const found = types.find(t => t.value === type);
    return found ? found.icon : '📝';
  },

  getNoteTypeLabel(type) {
    const types = this.getNoteTypes();
    const found = types.find(t => t.value === type);
    return found ? found.label : 'Note';
  },

  formatDate(dateString) {
    if (!dateString) return '';
    // Parse date parts manually to avoid timezone shift
    // Input format: "2025-12-31"
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  formatTime(timeString) {
    if (!timeString) return '';
    // Parse time string (HH:MM:SS or HH:MM:SS+TZ)
    const parts = timeString.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  },

  formatEventDateTime(eventDate, eventTime) {
    const dateStr = this.formatDate(eventDate);
    if (!eventTime) return dateStr;
    const timeStr = this.formatTime(eventTime);
    return `${dateStr} at ${timeStr}`;
  },
};

// Client Notes API
export const clientNotesService = {
  getNotes: (clientId, params = {}) => api.get(`/clients/${clientId}/notes/`, { params }),
  createNote: (clientId, noteData) => api.post(`/clients/${clientId}/notes/`, noteData),
  updateNote: (clientId, noteId, noteData) => api.put(`/clients/${clientId}/notes/${noteId}`, noteData),
  deleteNote: (clientId, noteId) => api.delete(`/clients/${clientId}/notes/${noteId}`),
};

// Projects API
export const projectService = {
  getProjects: () => api.get('/cases/projects'),
  createProject: (projectData) => api.post('/projects/', projectData),
  getProjectsByCase: (caseId) => api.get(`/cases/${caseId}/projects/`),
  getProject: (id) => api.get(`/projects/${id}`),
  updateProject: (id, projectData) => api.put(`/projects/${id}`, projectData),
  updateProjectStatus: (id, statusData) => api.patch(`/projects/${id}/status`, statusData),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  getProjectTasks: (id) => api.get(`/projects/${id}/tasks/`),
};

// Tasks API
export const taskService = {
  getTasksTableView: (params) => api.get('/tasks/table-view', { params }),
  getTasks: () => api.get('/tasks/'),
  getTask: (id) => api.get(`/tasks/${id}`),
  createTask: (taskData) => api.post('/tasks/', taskData),
  updateTask: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  updateTaskStatus: (id, statusData) => api.patch(`/tasks/${id}/status`, statusData),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  getTasksByProject: (projectId) => api.get(`/projects/${projectId}/tasks/`),
  getTasksByCase: (caseId) => api.get(`/cases/${caseId}/tasks/`),
  getTaskStatusHistory: (id) => api.get(`/tasks/${id}/status-history`),
  
  // Timer API
  startTimer: (id) => api.post(`/tasks/${id}/timer/start`),
  stopTimer: (id) => api.post(`/tasks/${id}/timer/stop`),
  getTimerStatus: (id) => api.get(`/tasks/${id}/timer`),

  // Time Logging API
  logTime: (id, timeLogData) => api.post(`/tasks/${id}/time-logs/`, timeLogData),
  getTimeLogs: (id) => api.get(`/tasks/${id}/time-logs/`),

  // Reminders API
  getReminders: (id) => api.get(`/tasks/${id}/reminders`),
  createReminder: (id, reminderData) => api.post(`/tasks/${id}/reminders`, reminderData),
  deleteReminder: (id, reminderId) => api.delete(`/tasks/${id}/reminders/${reminderId}`),

  // Reorder API
  reorderTasks: (tasks) => api.put('/tasks/reorder/batch', { tasks }),
};

// Standalone Time Tracking API
export const timeTrackingService = {
  // Timer — multi-timer endpoints (timer_id required)
  getTimers: () => api.get('/time-tracking/timers'),
  getTimer: () => api.get('/time-tracking/timer'),
  startTimer: (data = {}) => api.post('/time-tracking/timer/start', data),
  pauseTimer: (timerId) => api.post(`/time-tracking/timer/${timerId}/pause`),
  resumeTimer: (timerId) => api.post(`/time-tracking/timer/${timerId}/resume`),
  stopTimer: (timerId) => api.post(`/time-tracking/timer/${timerId}/stop`),
  discardTimer: (timerId) => api.post(`/time-tracking/timer/${timerId}/discard`),
  updateTimer: (timerId, data) => api.patch(`/time-tracking/timer/${timerId}`, data),

  // Time entries
  createEntry: (data) => api.post('/time-tracking/entries', data),
  createBulkEntries: (entries, loggedAt, userId) => api.post('/time-tracking/entries/bulk', { entries, logged_at: loggedAt, user_id: userId }),
  listEntries: (params = {}) => api.get('/time-tracking/entries', { params }),
  updateEntry: (id, data) => api.patch(`/time-tracking/entries/${id}`, data),
  deleteEntry: (id) => api.delete(`/time-tracking/entries/${id}`),
};

// Expenses API
export const expenseService = {
  list: (params = {}) => api.get('/expenses/', { params }),
  create: (data) => api.post('/expenses/', data),
  update: (id, data) => api.patch(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Calendar API (Chris's key feature)
export const calendarService = {
  getTodaysEvents: (userId) => api.get(`/calendar/events/user/${userId}/today`),
  getEvents: (params = {}) => api.get('/calendar/events/', { params }),
  getEventsInDateRange: (userId, startDate, endDate) => {
    const params = {
      start_date: startDate,
      end_date: endDate
    };
    if (userId) {
      params.user_id = userId;
    }
    return api.get('/calendar/events/', { params });
  },
  getEvent: (id) => api.get(`/calendar/events/${id}`),
  createEvent: (eventData, conflictResolution = {}) => {
    const requestBody = {
      ...eventData,
      conflict_resolution: conflictResolution
    };
    return api.post('/calendar/events/', requestBody);
  },
  updateEvent: (id, eventData, conflictResolution = {}) => {
    const requestBody = {
      ...eventData,
      conflict_resolution: conflictResolution
    };
    return api.put(`/calendar/events/${id}`, requestBody);
  },
  deleteEvent: (id, deleteRecurring = false) => api.delete(`/calendar/events/${id}`, {
    params: { delete_recurring: deleteRecurring }
  }),
  getTasksForCalendar: (userId, startDate, endDate) => {
    const params = { start_date: startDate, end_date: endDate };
    if (userId) params.user_id = userId;
    return api.get('/calendar/tasks/', { params });
  },

  // Google Calendar Integration
  googleCalendar: {
    getConnectionStatus: () => api.get('/auth/google/calendar/status'),
    initiateConnection: () => api.get('/auth/google/calendar/connect'),
    disconnect: () => api.delete('/auth/google/calendar/disconnect'),
    syncEvent: (eventId) => api.post(`/calendar/sync/events/${eventId}/sync`),
    fullSync: () => api.post('/calendar/sync/full-sync'),
    importFromGoogle: (daysBack = 30, daysForward = 180) => 
      api.post('/calendar/sync/import-from-google', {}, { params: { days_back: daysBack, days_forward: daysForward } }),
    getSyncLogs: (limit = 50) => api.get('/calendar/sync/logs', { params: { limit } }),
    poll: () => api.post('/calendar/sync/poll')
  },

  // Outlook Calendar Integration
  outlookCalendar: {
    getConnectionStatus: () => api.get('/auth/outlook/calendar/status'),
    initiateConnection: () => api.get('/auth/outlook/calendar/connect'),
    disconnect: () => api.delete('/auth/outlook/calendar/disconnect'),
    syncEvent: (eventId) => api.post(`/outlook/sync/events/${eventId}/sync`),
    fullSync: () => api.post('/outlook/sync/full-sync'),
    importFromOutlook: (daysBack = 30, daysForward = 180) =>
      api.post('/outlook/sync/import-from-outlook', {}, { params: { days_back: daysBack, days_forward: daysForward } }),
    getSyncLogs: (limit = 50) => api.get('/outlook/sync/logs', { params: { limit } }),
    poll: () => api.post('/outlook/sync/poll')
  }
};

// Document API
export const documentService = {
  getDocuments: (params = {}) => api.get('/documents/', { params }),
  getDocument: (id) => api.get(`/documents/${id}`),
  uploadDocument: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (id, documentData) => api.put(`/documents/${id}`, documentData),
  archiveDocument: (id, reason = null) => api.post(`/documents/${id}/archive`, { reason }),
  restoreDocument: (id) => api.post(`/documents/${id}/restore`),
  getDownloadUrl: (id, forceDownload = false) =>
    api.get(`/documents/${id}/download`, { params: { force_download: forceDownload } }),
  // AI processing endpoints
  getAiStatus: (id) => api.get(`/documents/${id}/ai/status`),
  processAi: (id) => api.post(`/documents/${id}/ai/process`),
  // Document Q&A endpoints
  askQuestion: (id, question) => api.post(`/documents/${id}/ask`, { question }),
  getQaHistory: (id) => api.get(`/documents/${id}/qa-history`),
  clearQaHistory: (id) => api.delete(`/documents/${id}/qa-history`),
  // Version management
  getVersions: (id) => api.get(`/documents/${id}/versions`),
  downloadVersion: (id, versionId) => api.get(`/documents/${id}/versions/${versionId}/download`),
  restoreVersion: (id, versionId) => api.post(`/documents/${id}/versions/${versionId}/restore`),
  replaceFile: (id, formData) => api.post(`/documents/${id}/replace`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Collabora editor
  getEditUrl: (id) => api.get(`/documents/${id}/edit-url`),
  // Sharing
  shareWithClient: (id, clientId, { note, notify } = {}) =>
    api.post(`/documents/${id}/share-with-client`, null, {
      params: { client_id: clientId, ...(note && { note }), notify: notify || 'none' }
    }),
  unshareWithClient: (id, clientId) =>
    api.delete(`/documents/${id}/unshare-with-client/${clientId}`),
  getSharingStatus: (id) =>
    api.get(`/documents/${id}/sharing-status`),
  // Legacy delete method - kept for compatibility but should use archive instead
  deleteDocument: (id) => api.post(`/documents/${id}/archive`, { reason: "Document deleted by user" }),
};

// Document Folders API
export const folderService = {
  // Get folder tree (nested structure) - requires case_id
  getFolderTree: (caseId) => api.get('/folders/tree', { params: { case_id: caseId } }),

  // List folders (flat or by parent)
  getFolders: (params = {}) => api.get('/folders/', { params }),

  // CRUD operations
  getFolder: (id) => api.get(`/folders/${id}`),
  createFolder: (folderData) => api.post('/folders/', folderData),
  updateFolder: (id, folderData) => api.put(`/folders/${id}`, folderData),
  deleteFolder: (id, force = false) => api.delete(`/folders/${id}`, { params: { force } }),

  // Move operations
  moveFolder: (id, newParentId) => api.post(`/folders/${id}/move`, {
    new_parent_folder_id: newParentId
  }),
  bulkMoveDocuments: (documentIds, folderId) => api.post('/folders/bulk-move-documents', {
    document_ids: documentIds,
    folder_id: folderId
  }),
};

// Template API (Enhanced!)
export const templateService = {
  // Existing endpoints
  getCaseTemplates: () => api.get('/templates/case-templates'),
  getCaseTemplatePreview: (templateId) => api.get(`/templates/case-templates/${templateId}/preview`),
  createCaseFromTemplate: (templateId, caseData) => api.post(`/templates/case-templates/${templateId}/create-case`, caseData),
  
  // Template Library
  getTemplateLibrary: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value);
    });
    return api.get(`/templates/library?${searchParams.toString()}`);
  },
  getLibraryTemplates: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value);
    });
    return api.get(`/templates/library?${searchParams.toString()}`);
  },
  importTemplate: (importData) => api.post('/templates/import', importData),
  
  // Template CRUD
  createCaseTemplate: (templateData) => api.post('/templates/case-templates', templateData),
  updateCaseTemplate: (templateId, templateData) => api.put(`/templates/case-templates/${templateId}`, templateData),
  deleteCaseTemplate: (templateId) => api.delete(`/templates/case-templates/${templateId}`),
  
  // Utilities
  getTemplateStats: () => api.get('/templates/stats'),
  getTemplateCategories: () => api.get('/templates/categories'),
  getPracticeAreas: () => api.get('/templates/practice-areas'),
  getProjectTemplates: () => api.get('/templates/project-templates'),
  createProjectTemplate: (templateData) => api.post('/templates/project-templates', templateData),
  rateTemplate: (templateId, ratingData) => api.post(`/templates/case-templates/${templateId}/rate`, ratingData),
  updateTaskTemplateAssignment: (pttId, assignment) => api.put(`/templates/project-task-templates/${pttId}/assignment`, assignment),
};

// Document Templates API
export const documentTemplateService = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value);
    });
    return api.get(`/document-templates?${searchParams.toString()}`);
  },
  get: (templateId) => api.get(`/document-templates/${templateId}`),
  create: (data) => api.post('/document-templates', data),
  update: (templateId, data) => api.put(`/document-templates/${templateId}`, data),
  delete: (templateId) => api.delete(`/document-templates/${templateId}`),
  getPlaceholders: () => api.get('/document-templates/placeholders'),
  generate: (templateId, data) => api.post(`/document-templates/${templateId}/generate`, data),
  preview: (templateId, data) => api.post(`/document-templates/${templateId}/preview`, data),
};

// Task Type Defaults API (Intelligent Scheduling)
export const taskTypeDefaultsService = {
  getAvailableTypes: () => api.get('/scheduling/task-type-defaults/available-types'),
  getEffectiveDefault: (taskType) => api.get(`/scheduling/task-type-defaults/effective/${taskType}`),
  getTaskTypeDefaults: (taskType = null) => {
    const params = taskType ? { task_type: taskType } : {};
    return api.get('/scheduling/task-type-defaults/', { params });
  },
  createTaskTypeDefault: (defaultData) => api.post('/scheduling/task-type-defaults/', defaultData),
  updateTaskTypeDefault: (defaultId, updateData) => api.put(`/scheduling/task-type-defaults/${defaultId}`, updateData),
  deleteTaskTypeDefault: (defaultId) => api.delete(`/scheduling/task-type-defaults/${defaultId}`),
  bulkUpdateUserDefaults: (defaults) => api.post('/scheduling/task-type-defaults/bulk-update', defaults),
};

// Trust API
export const trustService = {
  // Trust Account Management
  getTrustAccounts: (params = {}) => api.get('/trust/accounts/', { params }),
  getTrustAccount: (accountId) => api.get(`/trust/accounts/${accountId}`),
  createTrustAccount: (accountData) => api.post('/trust/accounts/', accountData),
  updateTrustAccount: (accountId, updateData) => api.put(`/trust/accounts/${accountId}`, updateData),

  // Trust Transactions
  getAccountTransactions: (accountId, params = {}) => api.get(`/trust/accounts/${accountId}/transactions/`, { params }),
  createTransaction: (transactionData) => api.post('/trust/transactions/', transactionData),
  updateTransaction: (transactionId, updateData) => api.put(`/trust/transactions/${transactionId}`, updateData),

  // Client Trust Deposit (payment without invoice)
  createClientDeposit: (clientId, depositData) => api.post(`/trust/clients/${clientId}/deposit`, depositData),

  // Client Trust Withdrawal (refund from trust)
  createClientWithdrawal: (clientId, withdrawalData) => api.post(`/trust/clients/${clientId}/withdrawal`, withdrawalData),

  // Void a trust transaction
  voidTransaction: (transactionId) => api.delete(`/trust/transactions/${transactionId}`),

  // Permanently delete a trust transaction
  deleteTransaction: (transactionId) => api.delete(`/trust/transactions/${transactionId}/permanent`),

  // Trust Case Allocations
  getAccountAllocations: (accountId) => api.get(`/trust/accounts/${accountId}/allocations/`),
  createAllocation: (allocationData) => api.post('/trust/allocations/', allocationData),

  // Trust Reporting
  getTrustSummary: () => api.get('/trust/summary'),

  // Helper methods for formatting
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  },

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  formatDateTime(dateString) {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  getTransactionTypeIcon(type) {
    const icons = {
      deposit: '💰',
      withdrawal: '💸',
      transfer: '↔️',
      interest: '📈',
      refund: '🔄',
      adjustment: '⚖️',
      fee_earned: '💼',
      voided_deposit: '🚫',
      voided_withdrawal: '🚫',
      voided_transfer: '🚫',
      voided_interest: '🚫',
      voided_refund: '🚫',
      voided_adjustment: '🚫',
      voided_fee_earned: '🚫',
    };
    return icons[type] || '💳';
  },

  getTransactionTypeColor(type) {
    const colors = {
      deposit: 'text-green-600',
      withdrawal: 'text-red-600',
      transfer: 'text-yellow-600',
      interest: 'text-purple-600',
      refund: 'text-yellow-600',
      adjustment: 'text-yellow-600',
      fee_earned: 'text-indigo-600',
      voided_deposit: 'text-gray-500',
      voided_withdrawal: 'text-gray-500',
      voided_transfer: 'text-gray-500',
      voided_interest: 'text-gray-500',
      voided_refund: 'text-gray-500',
      voided_adjustment: 'text-gray-500',
      voided_fee_earned: 'text-gray-500',
    };
    return colors[type] || 'text-gray-600';
  },

  getStatusColor(status) {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  getAccountStatusColor(status) {
    const colors = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      frozen: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
};

// Operating API
export const operatingService = {
  // Operating Account Management
  getAccounts: (params = {}) => api.get('/operating/accounts/', { params }),
  getAccount: (accountId) => api.get(`/operating/accounts/${accountId}`),
  createAccount: (accountData) => api.post('/operating/accounts/', accountData),
  updateAccount: (accountId, updateData) => api.put(`/operating/accounts/${accountId}`, updateData),

  // Operating Transactions
  getAccountTransactions: (accountId, params = {}) => api.get(`/operating/accounts/${accountId}/transactions/`, { params }),
  createTransaction: (transactionData) => api.post('/operating/transactions/', transactionData),
  updateTransaction: (transactionId, updateData) => api.put(`/operating/transactions/${transactionId}`, updateData),

  // Operating Summary
  getSummary: () => api.get('/operating/summary'),

  // Helper methods
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  },

  formatDate(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  getAccountStatusColor(status) {
    const colors = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
};

// Invoice API
export const invoiceService = {
  // Invoice CRUD Operations
  getInvoices: (params = {}) => api.get('/invoices/', { params }),
  getInvoice: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  createInvoice: (invoiceData) => api.post('/invoices/', invoiceData),
  createInvoiceFromTasks: (taskInvoiceData) => api.post('/invoices/from-tasks/', taskInvoiceData),
  updateInvoice: (invoiceId, updateData) => api.put(`/invoices/${invoiceId}`, updateData),
  deleteInvoice: (invoiceId) => api.delete(`/invoices/${invoiceId}`),

  // Line Item CRUD (draft invoices only)
  addLineItem: (invoiceId, lineItemData) => api.post(`/invoices/${invoiceId}/line-items`, lineItemData),
  updateLineItem: (invoiceId, lineItemId, data) => api.put(`/invoices/${invoiceId}/line-items/${lineItemId}`, data),
  deleteLineItem: (invoiceId, lineItemId) => api.delete(`/invoices/${invoiceId}/line-items/${lineItemId}`),

  // Invoice Actions
  sendInvoice: (invoiceId, email) => api.post(`/invoices/${invoiceId}/send`, { email }),
  batchSendInvoices: (invoiceIds) => api.post('/invoices/batch/send', { invoice_ids: invoiceIds }),
  voidInvoice: (invoiceId) => api.post(`/invoices/${invoiceId}/void`),

  // Approval Workflow
  submitForApproval: (invoiceId) => api.post(`/invoices/${invoiceId}/submit-for-approval`),
  approveInvoice: (invoiceId) => api.post(`/invoices/${invoiceId}/approve`),
  rejectInvoice: (invoiceId) => api.post(`/invoices/${invoiceId}/reject`),
  applyTrust: (invoiceId) => api.post(`/invoices/${invoiceId}/apply-trust`),
  batchSubmitForApproval: (invoiceIds) => api.post('/invoices/batch/submit-for-approval', { invoice_ids: invoiceIds }),
  batchApproveInvoices: (invoiceIds) => api.post('/invoices/batch/approve', { invoice_ids: invoiceIds }),
  batchApplyTrust: (invoiceIds) => api.post('/invoices/batch/apply-trust', { invoice_ids: invoiceIds }),
  batchReturnToDraft: (invoiceIds) => api.post('/invoices/batch/return-to-draft', { invoice_ids: invoiceIds }),
  batchVoidInvoices: (invoiceIds) => api.post('/invoices/batch/void', { invoice_ids: invoiceIds }),
  downloadInvoicePDF: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { 
    responseType: 'blob' 
  }),
  
  // Payment Management
  addPayment: (invoiceId, paymentData) => api.post(`/invoices/${invoiceId}/payments`, paymentData),
  
  // Statistics and Reports
  getInvoiceStats: (params = {}) => api.get('/invoices/stats', { params }),
  getClientBalances: () => api.get('/invoices/balances'),
  
  // Utility Methods
  getUnbilledTasks: (caseId) => api.get(`/invoices/cases/${caseId}/unbilled-tasks`),
  getUnbilledTime: (caseId) => api.get(`/invoices/cases/${caseId}/unbilled-time`),
  getUnbilledExpenses: (caseId) => api.get(`/invoices/cases/${caseId}/unbilled-expenses`),
  calculateLateFees: () => api.post('/invoices/calculate-late-fees'),
  
  // Helper methods for formatting and display
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  },

  formatDate(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  formatDateTime(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  getStatusColor(status) {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-orange-100 text-orange-800',
      approved: 'bg-blue-100 text-blue-800',
      sent: 'bg-gray-100 text-gray-800',
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      void: 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  getStatusIcon(status) {
    const icons = {
      draft: '📝',
      pending_approval: '🔍',
      approved: '✔️',
      sent: '📤',
      paid: '✅',
      partial: '⏳',
      overdue: '⚠️',
      void: '❌',
    };
    return icons[status] || '📄';
  },

  getStatusLabel(status) {
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      sent: 'Sent',
      paid: 'Paid',
      partial: 'Partial',
      overdue: 'Overdue',
      void: 'Void',
    };
    return labels[status] || status;
  },

  getLineItemTypeIcon(itemType) {
    const icons = {
      time: '⏰',
      expense: '💰',
      flat_fee: '📋',
      discount: '🏷️',
      late_fee: '⚡',
    };
    return icons[itemType] || '📄';
  },

  calculateBalance(invoice) {
    if (!invoice) return 0;
    const total = parseFloat(invoice.total_amount || 0);
    const paid = parseFloat(invoice.paid_amount || 0);
    return total - paid;
  },

  isOverdue(invoice) {
    if (!invoice || invoice.status === 'paid' || invoice.status === 'void') {
      return false;
    }
    return new Date(invoice.due_date) < new Date();
  },

  getDaysOverdue(invoice) {
    if (!this.isOverdue(invoice)) return 0;
    const today = new Date();
    const dueDate = new Date(invoice.due_date);
    const diffTime = Math.abs(today - dueDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
};

// Document Signing Service (JustSign)
export const signingService = {
  // Status check
  getStatus: () => api.get('/signing/status'),

  // Templates
  getTemplates: (params = {}) => api.get('/signing/templates/', { params }),
  getTemplate: (id) => api.get(`/signing/templates/${id}`),
  createTemplateFromPDF: (templateData) => api.post('/signing/templates/pdf', templateData),
  updateTemplate: (id, templateData) => api.put(`/signing/templates/${id}`, templateData),
  deleteTemplate: (id) => api.delete(`/signing/templates/${id}`),

  // Envelopes
  getEnvelopes: (params = {}) => api.get('/signing/envelopes/', { params }),
  getEnvelope: (id, refresh = false) => api.get(`/signing/envelopes/${id}`, { params: { refresh } }),
  createEnvelope: (envelopeData) => api.post('/signing/envelopes/', envelopeData),
  createEnvelopeFromUpload: (formData) => api.post('/signing/envelopes/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  createEnvelopeFromDocument: (data) => api.post('/signing/envelopes/from-document', data),
  refreshEnvelopeStatus: (id) => api.post(`/signing/envelopes/${id}/refresh`),
  resendEnvelope: (id, signerUpdates = null) => api.post(`/signing/envelopes/${id}/resend`, signerUpdates ? { signers: signerUpdates } : {}),
  downloadSignedDocument: (id) => api.get(`/signing/envelopes/${id}/download`),
  viewSignedDocument: (id) => api.get(`/signing/envelopes/${id}/download`, { params: { inline: true } }),

  // Helpers
  getStatusColor(status) {
    const colors = {
      draft: 'gray',
      sent: 'blue',
      viewed: 'yellow',
      partially_signed: 'orange',
      completed: 'green',
      declined: 'red',
      expired: 'gray',
      voided: 'red',
    };
    return colors[status] || 'gray';
  },

  getStatusLabel(status) {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      partially_signed: 'Partially Signed',
      completed: 'Completed',
      declined: 'Declined',
      expired: 'Expired',
      voided: 'Voided',
    };
    return labels[status] || status;
  },

  getStatusIcon(status) {
    const icons = {
      draft: '📝',
      sent: '📤',
      viewed: '👁️',
      partially_signed: '✍️',
      completed: '✅',
      declined: '❌',
      expired: '⏰',
      voided: '🚫',
    };
    return icons[status] || '📄';
  },
};

export const casePartyService = {
  getParties: (caseId) => api.get(`/cases/${caseId}/parties/`),
  addParty: (caseId, partyData) => api.post(`/cases/${caseId}/parties/`, partyData),
  updateParty: (caseId, partyId, partyData) => api.put(`/cases/${caseId}/parties/${partyId}`, partyData),
  removeParty: (caseId, partyId) => api.delete(`/cases/${caseId}/parties/${partyId}`),
};

export const personService = {
  getAll: (params) => api.get('/persons/', { params }),
  get: (id) => api.get(`/persons/${id}`),
  create: (data) => api.post('/persons/', data),
  delete: (id) => api.delete(`/persons/${id}`),
  search: (query) => api.get(`/persons/search?q=${encodeURIComponent(query)}`),
  update: (personId, data) => api.put(`/persons/${personId}`, data),
  addContact: (personId, contactData) => api.post(`/persons/${personId}/contacts/`, contactData),
  updateContact: (personId, contactId, data) => api.put(`/persons/${personId}/contacts/${contactId}`, data),
  deleteContact: (personId, contactId) => api.delete(`/persons/${personId}/contacts/${contactId}`),
  addRelationship: (personId, relData) => api.post(`/persons/${personId}/relationships/`, relData),
  deleteRelationship: (personId, relationshipId) => api.delete(`/persons/${personId}/relationships/${relationshipId}`),
  getRelationships: (id) => api.get(`/persons/${id}/relationships/`),
};

// Search API (Universal Search / Command Palette)
export const searchService = {
  search: (query, limit = 5) => api.get('/search/', { params: { q: query, limit } }),
};

export const conflictService = {
  checkConflicts: (data) => api.post('/cases/conflict-check', data),
  getHistory: (skip = 0, limit = 50) => api.get(`/cases/conflict-checks?skip=${skip}&limit=${limit}`),
};

// Convenience exports for password reset
export const requestPasswordReset = authService.requestPasswordReset;
export const validateResetToken = authService.validateResetToken;
export const resetPassword = authService.resetPassword;
export const verifyEmail = authService.verifyEmail;

// Async search helpers for AsyncSearchableSelect dropdowns
export async function searchClientsAsync(query) {
  const res = await clientService.getClients({ search: query || undefined, limit: 50 });
  const clients = res.data?.data || [];
  return clients.map(c => ({
    value: c.id,
    label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || 'Unnamed Client',
    sublabel: c.company_name || c.email || undefined,
  }));
}

export async function searchCasesAsync(query) {
  const res = await caseService.getCases({ search: query || undefined, limit: 100 });
  const cases = Array.isArray(res.data) ? res.data : res.data?.data || [];
  return cases.map(c => ({
    value: c.id,
    label: `${c.case_number} - ${c.title}`,
  }));
}

export default api;
